/**
 * External dependencies
 */
import { Fragment } from "react";
import dynamic from "next/dynamic";
import classNames from "classnames";

/**
 * Internal dependencies
 */
import { getStrapiMedia, getStrapiMediaArr } from "utils";
import TextImage from "components/text-image/text-image";
import GridBoxes from "components/grid-boxes/grid-boxes";
import Features from "components/features/features";
import HomeIntro from "blocks/home-intro/home-intro";
import TextBlock from "components/text-block/text-block";
const Video = dynamic(() => import("components/video/video"));
import Size from "components/size/size";
import Alternating from "blocks/alternating/alternating";
const HomeImageTabBlock = dynamic(() => import("blocks/home-image-tab/home-image-tab"));
import Logos from "components/logos/logos";
import Image from "next/image";
import Markdown from "components/markdown/markdown";
import Stack from "components/stack/stack";
import Support from "components/support/support";
import BtnLinkWithOptions from "components/btn-link/btn-link-with-options";
const Tabs = dynamic(() => import("blocks/tabs/tabs"));
const SliderElement = dynamic(() => import("blocks/slider-element/slider-element"));
import Projects from "components/projects/projects";
import Banner from "blocks/banner/banner";
import Description from "blocks/description/description";
const Gallery = dynamic(() => import("blocks/gallery/gallery"));
const Accordion = dynamic(() => import("blocks/accordion/accordion"));
import Team from "blocks/team/team";
import Testimonial from "blocks/testimonial/testimonial";
const Numbers = dynamic(() => import("blocks/numbers/numbers"));
import Images from "blocks/images/images";
import HeadingRow from "blocks/heading-row/heading-row";
import IntroSlideText from "blocks/intro-slide-text/intro-slide-text";
import PromiseDynamic from "components/promise/promise";
import StoreImages from "components/store-images/store-images";
const FormBuilder = dynamic(() => import("blocks/form-builder/form-builder"));
import IconCards from "blocks/icon-cards/icon-cards";
import PriceCards from "blocks/price-cards/price-cards";
import TrustedByGrowth from "blocks/growth/trusted-by-growth";
import IconCardsWithMedia from "blocks/icon-cards/icon-cards-with-media";
import IconCardsExtra from "blocks/icon-cards/icon-cards-extra";
const ScrollText = dynamic(() => import("blocks/scroll-text/scroll-text"));
import PercentCards from "blocks/percent-card/percent-card";

const DynamicSection = ({ data, removeClass, slug, pageHeading = false }: any) => {
  const firstVisibleSections = (data || [])
    .map((section: any, index: number) => !section.hide && !section.HideSection ? index : -1)
    .filter((index: number) => index !== -1)
    .slice(0, 2);
  const firstVisibleSection = firstVisibleSections[0];
  const handleDynamicComponent = (item: any, index: number) => {
    if (item.hide) {
      return null;
    }

    switch (item["__component"]) {
      case "sections.tabs":
        return (
          <HomeImageTabBlock
            key={index}
            data={item.ImageTabs}
            title={item.ImageTabTitle}
            link={{ text: item.ImageTabLinkText, url: item.ImageTabLinkURL }}
          />
        );
      case "sections.intro-slide-text":
        return <IntroSlideText key={index} data={item} />;
      case "sections.tabs-list":
        return <Tabs key={index} data={item} />;
      case "home-intro.home-intro":
        return <HomeIntro data={item} priority={index === firstVisibleSection} pageHeading={pageHeading && index === firstVisibleSection} key={index} />;
      case "sections.stores":
        return <StoreImages key={index} title={item.title} data={item.store} />;
      case "sections.logos":
        return <Logos logos={item.LogosImages.data} item={item} key={index} />;
      case "pr.pr":
        const promiseData = item.PromiseBoxes?.map((item: any) => ({
          title: item.Title,
          icon: item.Icon?.data ? (
            <Image
              alt=""
              src={getStrapiMedia(item.Icon)}
              width={24}
              height={24}
            />
          ) : (
            ""
          ),
          message: <Markdown content={item.Content} />,
        }));
        return (
          <PromiseDynamic
            key={index}
            data={promiseData}
            subtitle={item.Subtitle}
            title={item.Title}
            link={item.Link}
          />
        );
      case "sections.stack":
        const stackData = item.StackPartners?.data?.map((item: any) => (
          <Image
            alt=""
            src={getStrapiMediaArr(item)}
            width={114}
            height={38}
            key={item.id}
          />
        ));
        return (
          <Stack logos={stackData} item={item} key={index}>
            <Markdown content={item.StackTitle} />
            <Markdown content={item.StackContent} />
          </Stack>
        );
      case "general.sliders":
        return <SliderElement data={item} priority={firstVisibleSections.includes(index)} key={index} />;
      case "support.support":
        return (
          <Support
            key={index}
            image={
              item.Image.data && {
                src: getStrapiMedia(item.Image),
                width: 763,
                height: 461,
              }
            }
            link={<BtnLinkWithOptions data={item.Link}>{undefined}</BtnLinkWithOptions>}
          >
            <h3>{item.Title}</h3>
            <Markdown content={item.Content} />
          </Support>
        );
      case "video.video":
        return <Video VideoBlock={item} key={index} />;
      case "size.size":
        return <Size data={item} key={index} />;
      case "text-block.text-block":
        return <TextBlock data={item} key={index} />;
      case "boxes-section.boxes-section":
        return <GridBoxes boxes={item.BoxItem} key={index} />;
      case "text-image.text-image":
        return <TextImage data={item} key={index} />;
      case "feature-row.feature-row":
        return <Features boxes={item} key={index} />;
      case "sections.projects":
        return <Projects data={item} priority={firstVisibleSections.includes(index)} key={index} />;
      case "sections.description":
        return <Description data={item} key={index} />;
      case "sections.gallery":
        return <Gallery data={item} key={index} />;
      case "sections.banner":
        return <Banner data={item} priority={index === firstVisibleSection} key={index} />;
      case "alternate.alternate":
        return <Alternating data={item} key={index} />;
      case "sections.accordion":
        return <Accordion data={item} key={index} />;
      case "sections.team":
        return <Team data={item} key={index} />;
      case "sections.testimonial":
        return <Testimonial data={item} key={index} />;
      case "sections.heading-row":
        return <HeadingRow data={item} key={index} />;
      case "sections.numbers":
        return <Numbers data={item} key={index} />;
      case "sections.images":
        return <Images data={item} key={index} />;
      case "form-builder.form-builder":
        return <FormBuilder data={item} slug={slug} key={index} />;
      case "icon-card.icon-card":
        return <IconCards data={item} key={index} />;
      case "icon-card.icon-card-media":
        return <IconCardsWithMedia data={item} key={index} />;
      case "icon-card.icon-card-extra":
        return <IconCardsExtra data={item} key={index} />;
      case "price-card.pricing":
        return <PriceCards data={item} key={index} />;
      case "growth.trusted-by-growth":
        return <TrustedByGrowth data={item} key={index} />;
      case "scroll-text.scroll-text":
        return <ScrollText data={item} key={index} />;
      case "percent-card.percent-card":
        return <PercentCards data={item} key={index} />;
      default:
        return <Fragment key={index}></Fragment>;
    }
  };
  return (
    <div className={classNames({ "dynamic-section": !removeClass })}>
      {data?.map((item: any, index: number) =>
        handleDynamicComponent(item, index)
      )}
    </div>
  );
};

export default DynamicSection;
